import { useEffect, useState, useMemo, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faMoneyBill1,
  faCalendarAlt,
  faAngleLeft,
  faAngleRight,
  faArrowsRotate,
  faPen,
  faPlus
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../components/usercontext/user_context";

export default function ViewCompanyVacancies() {
  const { id } = useParams();
  const { user } = useContext(UserContext);
  const { t } = useTranslation();

  const [company, setCompany] = useState(null);
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");

  const isOwner =
    user && String(user.id) === String(id) && user.role === "company";

  const [page, setPage] = useState(1);
  const vacanciesPerPage = 20;

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const res = await fetch(`/api/company_vacancies.php?id=${id}`);
        const data = await res.json();
        if (data.success) {
          setCompany(data.company);
          setVacancies(data.vacancies);
        }
      } catch (err) {
        console.error("Error loading company data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyData();
  }, [id]);

  // Проверка срока в 30 дней
  const checkIsExpired = (createdAt) => {
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    if (isNaN(createdDate.getTime())) return false;

    const now = new Date();
    const diffInDays = (now - createdDate) / (1000 * 60 * 60 * 24);
    return diffInDays > 30;
  };

  // Функция для отправки запроса на переопубликацию
  const handleRepublish = async (vacancyId) => {
    try {
      const res = await fetch("/api/republish_vacancy.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ vacancy_id: vacancyId, user_id: user.id }),
      });

      const data = await res.json();

      if (data.success) {
        // Локально обновляем дату в стейте, чтобы вакансия сразу переместилась в активные
        setVacancies((prev) =>
          prev.map((vac) =>
            vac.id === vacancyId
              ? { ...vac, created_at: new Date().toISOString() }
              : vac,
          ),
        );
      } else {
        alert(data.message || t("Error"));
      }
    } catch (err) {
      console.error("Republish request failed:", err);
    }
  };

  // ИСПРАВЛЕНО: Фильтрация теперь опирается исключительно на срок в 30 дней (так как поля status в БД нет)
  const filteredVacancies = useMemo(() => {
    return vacancies.filter((vac) => {
      const isExpired = checkIsExpired(vac.created_at);

      // Посетители видят только НЕ просроченные вакансии
      if (!isOwner) return !isExpired;

      // Владелец переключает вкладки "Активные" (не просроченные) и "Архив" (просроченные)
      if (filter === "active") {
        return !isExpired;
      } else {
        return isExpired;
      }
    });
  }, [vacancies, filter, isOwner]);

  const totalPages = Math.ceil(filteredVacancies.length / vacanciesPerPage);
  const currentVacancies = filteredVacancies.slice(
    (page - 1) * vacanciesPerPage,
    page * vacanciesPerPage,
  );

  if (loading)
    return (
      <div className="w-full h-[300px] flex justify-center items-center">
        <p className="text-[#006674] text-xl font-semibold animate-pulse">
          {t("loading")}...
        </p>
      </div>
    );

  if (!company)
    return (
      <div className="text-center text-red-500 mt-10">
        {t("Company not found")}
      </div>
    );

  return (
    <div className="p-6 flex justify-start flex-col w-full">
      <div className="w-full flex justify-between items-center">
        <h3 className="text-3xl font-bold text-white uppercase mb-4">
          {isOwner ? t("My Job Posts") : t("companyvacancies")}
        </h3>

        <Link
          to={`/AddVacancy`}
          className="flex justify-center opacity-0 animate-[showtitle_1s_forwards] items-center py-2 gap-3 px-3 max-sm:p-0 max-sm:w-[40px] max-sm:text-sm max-sm:h-[40px] rounded-full bg-[#00667480] border-2 border-white 
                           text-white text-xl hover:bg-[#006674aa] transition-all duration-300"
          title={t("Add News")}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span className="max-sm:hidden"> {t("post")}</span>
        </Link>
      </div>

      {isOwner && (
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => {
              setFilter("active");
              setPage(1);
            }}
            className={`px-4 py-2 rounded border transition-all duration-300 font-semibold ${
              filter === "active"
                ? "bg-white text-[#006674]"
                : "border border-white text-white hover:bg-white/10"
            }`}
          >
            {t("active")}
          </button>
          <button
            onClick={() => {
              setFilter("inactive");
              setPage(1);
            }}
            className={`px-4 py-2 rounded border transition-all duration-300 font-semibold ${
              filter === "inactive"
                ? "bg-white text-[#006674]"
                : "border border-white text-white hover:bg-white/10"
            }`}
          >
            {t("archive")}
          </button>
        </div>
      )}

      {currentVacancies.length > 0 ? (
        <>
          <div className="w-full flex flex-wrap gap-6 items-center justify-start">
            {currentVacancies.map((vac) => {
              const isExpired = checkIsExpired(vac.created_at);
              const formattedDate = !isNaN(new Date(vac.created_at).getTime())
                ? new Date(vac.created_at).toLocaleDateString()
                : new Date().toLocaleDateString();

              return (
                <article
                  key={vac.id}
                  className="w-[440px] border-[1px] border-[#006674] overflow-hidden max-lg:w-[350px] max-md:w-full pt-3 flex flex-col justify-between rounded-lg shadow-lg bg-white cursor-pointer relative transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Верхняя контентная часть */}
                  <div className="w-full flex gap-[13px] sm:gap-6 px-3 items-center mb-3">
                    <img
                      className="w-[64px] h-[64px] sm:w-[90px] sm:h-[90px] object-contain rounded-xl border-[4px] border-white shadow-lg bg-gray-200"
                      src={`/api/uploads/${vac.companyLogo}`}
                      alt={vac.companyName}
                    />
                    <ul className="flex flex-col gap-1">
                      <li>
                        <Link
                          to={`/vacancy/${vac.id}`}
                          className="text-xl text-[#006674] font-semibold hover:underline line-clamp-1"
                        >
                          {vac.workname}
                        </Link>
                      </li>
                      <li className="text-gray-600 line-clamp-1">
                        {vac.companyName}
                      </li>
                      <li className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                        <FontAwesomeIcon
                          className="text-[#006674] w-4"
                          icon={faLocationDot}
                        />
                        <span className="line-clamp-1">{vac.workplace}</span>
                      </li>
                      <li className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                        <FontAwesomeIcon
                          className="text-[#006674] w-4"
                          icon={faMoneyBill1}
                        />
                        <span>
                          {vac.salary
                            ? `${vac.salary} ${vac.currency === "AMD" ? "֏" : vac.currency === "USD" ? "$" : "₽"}`
                            : t("Not specified")}
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Нижняя панель управления в едином стиле */}
                  <div className="w-full p-2 px-3 flex justify-between items-center bg-[#006674] text-white text-sm sm:text-base">
                    {/* Дата публикации */}
                    <span className="text-xs sm:text-sm">
                      <FontAwesomeIcon className="mr-1" icon={faCalendarAlt} />
                      {formattedDate}
                    </span>

                    {/* Правый блок управления (Кнопки действий) */}
                    <div className="flex items-center gap-2 sm:gap-4">
                      {/* КНОПКА РЕДАКТИРОВАНИЯ ВАКАНСИИ */}
                      {isOwner && (
                        <Link
                          to={`/EditVacancy/${vac.id}`}
                          onClick={(e) => e.stopPropagation()} // Чтобы клик не срабатывал на всю карточку
                          className="flex items-center gap-1 bg-[#ffffff22] hover:bg-[#ffffff44] transition-all压缩 px-2 py-1 rounded-md text-xs sm:text-sm font-medium border border-white/20"
                          title={t("edit") || "Редактировать"}
                        >
                          <FontAwesomeIcon
                            icon={faPen}
                            className="text-[10px] sm:text-xs"
                          />
                          <span>{t("edit") || "Редактировать"}</span>
                        </Link>
                      )}

                      {/* Кнопка Переопубликовать ИЛИ Читать далее */}
                      {isOwner && isExpired ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRepublish(vac.id);
                          }}
                          className="bg-white text-[#006674] px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-bold hover:bg-gray-200 transition-colors flex items-center gap-1 sm:gap-2"
                        >
                          <FontAwesomeIcon icon={faArrowsRotate} />
                          {t("republish")}
                        </button>
                      ) : (
                        <Link
                          to={`/vacancy/${vac.id}`}
                          className="hover:underline font-semibold text-xs sm:text-sm"
                        >
                          {t("seemore")}
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={`px-4 py-2 rounded-lg ${page === 1 ? "bg-gray-400 cursor-not-allowed" : "bg-[#006674] hover:bg-[#00555a]"} text-white transition`}
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-white font-semibold">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-4 py-2 rounded-lg ${page === totalPages ? "bg-gray-400 cursor-not-allowed" : "bg-[#006674] hover:bg-[#00555a]"} text-white transition`}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-white bg-white/20 p-4 rounded">
          {t("Novacanciesfound")}
        </p>
      )}
    </div>
  );
}
